import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { tradingAPI } from '../../services/api';
import { TRADING_COLORS as C } from './constants';
import CoinIcon from './CoinIcon';

const TradeHistory = ({ mode }) => {
  const [loading, setLoading] = useState(true);
  const [spotHistory, setSpotHistory] = useState([]);
  const [futuresHistory, setFuturesHistory] = useState([]);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        if (mode === 'spot') {
          const res = await tradingAPI.getSpotHistory(50, 0);
          setSpotHistory(res.data || []);
        } else {
          const res = await tradingAPI.getTradeHistory('futures', 50, 0);
          setFuturesHistory(res.data?.trades || []);
        }
      } catch (e) {
        console.error('Failed to load history:', e);
      }
      setLoading(false);
    };
    loadHistory();
  }, [mode]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.textSecondary }}>Загрузка...</div>;
  }

  // Spot History
  if (mode === 'spot') {
    if (spotHistory.length === 0) {
      return <div style={{ padding: '60px 24px', textAlign: 'center', color: C.textTertiary, fontSize: 14 }}>Нет истории спот-операций</div>;
    }
    
    return (
      <div style={{ padding: 12, height: '100%', overflowY: 'auto' }}>
        {spotHistory.map((t, i) => {
          const isBuy = t.type === 'buy';
          const date = new Date(t.created_at);
          
          return (
            <div key={i} style={{ background: C.bgSecondary, borderRadius: 10, padding: 14, marginBottom: 8, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CoinIcon symbol={t.symbol + 'USDT'} size={32} />
                  <div>
                    <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{t.symbol}</div>
                    <div style={{ color: isBuy ? C.long : C.short, fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                      {isBuy ? 'Покупка' : 'Продажа'}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{t.quantity.toFixed(6)}</div>
                  <div style={{ color: C.yellow, fontSize: 12, marginTop: 2 }}>${t.total.toFixed(2)}</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                <span style={{ color: C.textTertiary, fontSize: 11 }}>Цена: ${t.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                <span style={{ color: C.textTertiary, fontSize: 11 }}>{date.toLocaleDateString('ru-RU')} {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Futures History
  if (futuresHistory.length === 0) {
    return <div style={{ padding: '60px 24px', textAlign: 'center', color: C.textTertiary, fontSize: 14 }}>Нет истории позиций</div>;
  }

  return (
    <div style={{ padding: 12, height: '100%', overflowY: 'auto' }}>
      {futuresHistory.map((t, i) => {
        const isLong = t.side === 'long';
        const pnl = t.pnl || 0;
        const isProfit = pnl >= 0;
        const closedDate = t.closed_at ? new Date(t.closed_at) : null;
        const base = t.symbol?.replace('USDT', '') || 'BTC';
        
        return (
          <div key={i} style={{ background: C.bgSecondary, borderRadius: 10, padding: 14, marginBottom: 8, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CoinIcon symbol={t.symbol} size={32} />
                <div>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{base}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ color: isLong ? C.long : C.short, fontSize: 11, fontWeight: 600 }}>{isLong ? 'Long' : 'Short'}</span>
                    <span style={{ fontSize: 10, color: C.yellow, background: C.yellowSoft, padding: '2px 4px', borderRadius: 3 }}>{t.leverage}x</span>
                    <span style={{ fontSize: 10, color: t.status === 'closed' ? C.long : C.short, background: t.status === 'closed' ? C.longBg : C.shortBg, padding: '2px 6px', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                      {t.status === 'closed' && <Check size={10} />}
                      {t.status === 'closed' ? 'Закрыта' : 'Отменена'}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: isProfit ? C.long : C.short, fontSize: 14, fontWeight: 700 }}>{isProfit ? '+' : ''}{pnl.toFixed(2)} USDT</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Вход</div>
                <div style={{ color: C.text, fontSize: 12 }}>${t.entry_price?.toLocaleString()}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Выход</div>
                <div style={{ color: C.text, fontSize: 12 }}>${(t.exit_price || t.close_price || t.current_price)?.toLocaleString() || '-'}</div>
              </div>
            </div>
            {closedDate && (
              <div style={{ color: C.textTertiary, fontSize: 10, marginTop: 6, textAlign: 'right' }}>
                {closedDate.toLocaleDateString('ru-RU')} {closedDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TradeHistory;
